import { useMemo, useState } from 'react'
import { requestPhoneOtp, verifyPhoneOtp } from '../../api/security'
import { createStripeConnectedAccount, createStripeOnboardingLink } from '../../api/stripeConnect'

type SellerEligibility = {
  can_list: boolean
  can_publish: boolean
  can_withdraw: boolean
  requirements: {
    email_verified: boolean
    phone_verified: boolean
    wallet_linked: boolean
    stripe_connect_ready: boolean
  }
  blockers: Array<{ code: string; message: string }>
}

type Props = {
  me: {
    seller_eligibility?: SellerEligibility
  } | null
  connectedAccount?: {
    account_id: string
    charges_enabled: boolean
    payouts_enabled: boolean
  } | null
  onRefresh?: () => void
}

export default function SellerOnboardingPanel({ me, connectedAccount, onRefresh }: Props) {
  const eligibility = me?.seller_eligibility
  const requirements = eligibility?.requirements || {
    email_verified: false,
    phone_verified: false,
    wallet_linked: false,
    stripe_connect_ready: false,
  }

  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [statusMsg, setStatusMsg] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)

  const rows = useMemo(() => ([
    { label: 'Email verified', ok: requirements.email_verified },
    { label: 'Phone verified', ok: requirements.phone_verified },
    { label: 'Wallet linked', ok: requirements.wallet_linked },
    { label: 'Stripe Connect', ok: requirements.stripe_connect_ready },
  ]), [requirements])

  async function onSendCode() {
    setErrorMsg(null)
    setStatusMsg(null)
    setSending(true)
    try {
      await requestPhoneOtp({ phone: phone || undefined })
      setOtpSent(true)
      setStatusMsg('Verification code sent.')
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to send code'
      setErrorMsg(msg)
    } finally {
      setSending(false)
    }
  }

  async function onVerifyCode() {
    if (!code.trim()) return
    setErrorMsg(null)
    setStatusMsg(null)
    setVerifying(true)
    try {
      await verifyPhoneOtp({ code: code.trim() })
      setOtpSent(false)
      setCode('')
      setStatusMsg('Phone verified.')
      onRefresh?.()
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Verification failed'
      setErrorMsg(msg)
    } finally {
      setVerifying(false)
    }
  }

  async function onConnectStripe() {
    setErrorMsg(null)
    setStatusMsg(null)
    setConnecting(true)
    try {
      if (!connectedAccount) {
        await createStripeConnectedAccount()
      }
      const { url } = await createStripeOnboardingLink()
      if (url) window.location.href = url
    } catch (e: any) {
      const msg = e?.response?.data || e?.message || 'Stripe connect failed'
      setErrorMsg(String(msg))
    } finally {
      setConnecting(false)
    }
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold">Seller onboarding</div>
        {eligibility && (
          <span className={`text-xs px-2 py-1 rounded-full border ${eligibility.can_publish ? 'border-emerald-400/50 text-emerald-200 bg-emerald-500/10' : 'border-amber-400/40 text-amber-200 bg-amber-500/10'}`}>
            {eligibility.can_publish ? 'Ready to publish' : 'Setup required'}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-3 py-2">
            <span>{row.label}</span>
            <span className={row.ok ? 'text-emerald-300' : 'text-rose-300'}>
              {row.ok ? 'Verified' : 'Missing'}
            </span>
          </div>
        ))}
      </div>

      {!requirements.phone_verified && (
        <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
          <div className="text-sm font-semibold">Verify phone</div>
          <div className="flex flex-wrap gap-2">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+15551234567"
              className="flex-1 min-w-[220px] rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/40"
            />
            <button
              onClick={onSendCode}
              disabled={sending}
              className="px-3 py-2 rounded-md bg-brand text-white text-sm font-semibold disabled:opacity-60"
            >
              {sending ? 'Sending...' : 'Send code'}
            </button>
          </div>
          {otpSent && (
            <div className="flex flex-wrap gap-2">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter code"
                className="flex-1 min-w-[160px] rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/40"
              />
              <button
              onClick={onVerifyCode}
              disabled={verifying || !code.trim()}
              className="px-3 py-2 rounded-md border border-white/10 bg-white/5 text-sm font-semibold disabled:opacity-60"
            >
              {verifying ? 'Verifying...' : 'Verify'}
            </button>
            </div>
          )}
        </div>
      )}

      {!requirements.wallet_linked && (
        <div className="text-sm text-white/70">
          Link a wallet to publish listings. <a className="text-brand-300 hover:underline" href="/settings/wallet">Open wallet settings</a>
        </div>
      )}

      {!requirements.stripe_connect_ready && (
        <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm">
          <div>Stripe Connect is required to withdraw earnings.</div>
          <button
            onClick={onConnectStripe}
            disabled={connecting}
            className="px-3 py-1.5 rounded-md border border-white/10 bg-white/5 font-semibold disabled:opacity-60"
          >
            {connecting ? 'Opening...' : 'Connect'}
          </button>
        </div>
      )}

      {statusMsg && <div className="text-xs text-emerald-300">{statusMsg}</div>}
      {errorMsg && <div className="text-xs text-rose-300">{errorMsg}</div>}
    </div>
  )
}
